import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { useCourierPickup } from '@/api/domains/courier-pickups';
import type { CourierPickup } from '@/api/types/pickups';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { formatWarsawClock } from './pickup-time';
import { PickupStatusBadge } from './PickupStatusBadge';

type Props = {
  /** The row clicked - used for instant display while the live fetch resolves. */
  initial: CourierPickup | null;
  onOpenChange: (open: boolean) => void;
  onCancel: (pickup: CourierPickup) => void;
};

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <span className="text-sm text-black">{value}</span>
    </div>
  );
}

export function PickupOrderDetailDialog({ initial, onOpenChange, onCancel }: Props) {
  const { t } = useTranslation('pickup-orders');
  const live = useCourierPickup(initial?.id ?? null);
  const pickup = live.data ?? initial;

  const formatDate = (iso: string) => format(parseISO(iso), 'dd.MM.yyyy');

  return (
    <Dialog open={initial !== null} onOpenChange={(open) => !open && onOpenChange(false)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('detail.title')}</DialogTitle>
          <DialogDescription>{t('detail.description')}</DialogDescription>
        </DialogHeader>

        {pickup ? (
          <div className="flex flex-col gap-5">
            <div className="flex items-center justify-between">
              <PickupStatusBadge status={pickup.status} group={pickup.status_group} />
              {pickup.tracking_number ? (
                <span className="text-sm text-muted-foreground">
                  {t('detail.tracking')}: {pickup.tracking_number}
                </span>
              ) : null}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Row label={t('detail.pickupPoint')} value={pickup.pickup_location?.name ?? '—'} />
              <Row
                label={t('detail.contactPerson')}
                value={pickup.contact_person?.full_name ?? '—'}
              />
              <Row label={t('detail.dateCreated')} value={formatDate(pickup.created_at)} />
              <Row
                label={t('detail.collectionWindow')}
                value={`${formatDate(pickup.pickup_time_from)} ${formatWarsawClock(pickup.pickup_time_from)}–${formatWarsawClock(pickup.pickup_time_to)}`}
              />
              <Row label={t('detail.shipmentCount')} value={pickup.estimated_count} />
              <Row
                label={t('detail.totalWeight')}
                value={`${pickup.estimated_weight} ${pickup.weight_unit.toLowerCase()}`}
              />
              {pickup.pickup_location ? (
                <Row
                  label={t('detail.address')}
                  value={`${pickup.pickup_location.street} ${pickup.pickup_location.house_number}, ${pickup.pickup_location.postal_code} ${pickup.pickup_location.city}`}
                />
              ) : null}
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('detail.close')}
          </Button>
          {pickup?.is_cancellable ? (
            <Button
              className="bg-[#e11d48] text-white hover:bg-[#be123c]"
              onClick={() => onCancel(pickup)}
            >
              {t('detail.cancelOrder')}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
